import { ArchivedGroupOrder, ArchivedReport, ArchivedReportPaginatedViewModel } from '@tfo/common/src/interfaces'
import * as FileSaver from 'file-saver'
import * as React from 'react'
import { IntlShape } from 'react-intl'
import XLSX from 'sheetjs-style'
import $http from '../../../../utils/$http'

const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8'
const fileExtension = '.xlsx'
const exportPageSize = 1000
const progressDisplayDelayMs = 500

export interface ArchiveExportProgress {
    fetched: number
    total: number
}

interface UseArchiveExcelExportParams {
    searchString: string
    sortBy: string
    startDate: Date | null
    endDate: Date
    intl: IntlShape
}

export const downloadAsExcel = (mappedRows: any[], filenamePrefix: string): void => {
    const ws = XLSX.utils.json_to_sheet(mappedRows)
    const wb = { Sheets: { 'data': ws }, SheetNames: ['data'] }
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
    const data = new Blob([excelBuffer], { type: fileType })
    FileSaver.saveAs(data, filenamePrefix + fileExtension)
}

export const useArchiveExcelExport = (params: UseArchiveExcelExportParams) => {
    const { searchString, sortBy, startDate, endDate, intl } = params

    const [isExporting, setIsExporting] = React.useState<boolean>(false)
    const [exportError, setExportError] = React.useState<boolean>(false)
    const [exportProgress, setExportProgress] = React.useState<ArchiveExportProgress | null>(null)
    const [showProgressBar, setShowProgressBar] = React.useState<boolean>(false)

    // Pages through the backend rather than requesting everything in one call, so large exports
    // don't rely on a single slow/timeout-prone request.
    const fetchAllForExport = async <T,>(endpoint: string): Promise<{ items: T[], hasMultipleCompanies: boolean }> => {
        const items: T[] = []
        let hasMultipleCompanies = false
        let page = 1
        let totalRowCount = Infinity
        while (items.length < totalRowCount) {
            const response = await $http.post<ArchivedReportPaginatedViewModel<T>>(`${endpoint}?searchText=${searchString}&sortBy=${sortBy}&currentPage=${page}&startDate=${startDate !== null ? startDate.toJSON() : ''}&endDate=${endDate.toJSON()}&rowsPerPage=${exportPageSize}`)
            items.push(...response.items)
            totalRowCount = response.totalRowCount
            hasMultipleCompanies = response.hasMultipleCompanies
            setExportProgress({ fetched: items.length, total: totalRowCount })
            page++
        }
        return { items, hasMultipleCompanies }
    }

    const buildReportRow = (i: ArchivedReport, hasMultipleCompanies: boolean) => ({
        ...(hasMultipleCompanies ? { [intl.formatMessage({ id: 'common.company' })]: i.companyName } : {}),
        [intl.formatMessage({ id: 'common.reportPackage' })]: i.reportPackageName,
        [intl.formatMessage({ id: 'common.creator' })]: i.creatorName,
        [intl.formatMessage({ id: 'common.individualId' })]: i.controlPerson,
        [intl.formatMessage({ id: 'readreport.label.individualreference' })]: i.individualReference ?? '',
        [intl.formatMessage({ id: 'common.ordered' })]: i.created ? intl.formatDate(i.created) : '',
        [intl.formatMessage({ id: 'common.delivered' })]: i.delivered ? intl.formatDate(i.delivered) : '-',
    })

    const buildGroupOrderRow = (i: ArchivedGroupOrder, hasMultipleCompanies: boolean) => ({
        ...(hasMultipleCompanies ? { [intl.formatMessage({ id: 'common.company' })]: i.companyName } : {}),
        [intl.formatMessage({ id: 'common.reportPackage' })]: i.reportPackage,
        [intl.formatMessage({ id: 'common.creator' })]: i.creatorName,
        [intl.formatMessage({ id: 'common.noOfIndividuals' })]: i.reportCount,
        [intl.formatMessage({ id: 'common.ordered' })]: i.created ? intl.formatDate(i.created) : '',
        [intl.formatMessage({ id: 'common.delivered' })]: i.delivered ? intl.formatDate(i.delivered) : '-',
        [intl.formatMessage({ id: 'common.reportWithDiscrepancy' })]: `${i.reportWithDiscrepancyCount}/${i.reportCount}`,
    })

    const runExport = async (exportFn: () => Promise<void>): Promise<void> => {
        if (isExporting) return
        setIsExporting(true)
        setExportError(false)
        setExportProgress(null)
        // Only surface the progress bar once the export has been running for a while, so
        // fast exports don't just flash it on screen.
        const progressDisplayTimer = setTimeout(() => setShowProgressBar(true), progressDisplayDelayMs)
        try {
            await exportFn()
        } catch (errorMessage) {
            console.error(errorMessage)
            setExportError(true)
        } finally {
            clearTimeout(progressDisplayTimer)
            setIsExporting(false)
            setExportProgress(null)
            setShowProgressBar(false)
        }
    }

    const exportSingleOrders = (): Promise<void> => runExport(async () => {
        const { items, hasMultipleCompanies } = await fetchAllForExport<ArchivedReport>('/ReportArchive/search')
        downloadAsExcel(items.map((i) => buildReportRow(i, hasMultipleCompanies)), 'Enskilda beställningar')
    })

    const exportGroupOrders = (): Promise<void> => runExport(async () => {
        const { items, hasMultipleCompanies } = await fetchAllForExport<ArchivedGroupOrder>('/ReportArchive/search/group')
        downloadAsExcel(items.map((i) => buildGroupOrderRow(i, hasMultipleCompanies)), 'Gruppbeställningar')
    })

    const exportOrganizationOrders = (): Promise<void> => runExport(async () => {
        const { items, hasMultipleCompanies } = await fetchAllForExport<ArchivedReport>('/ReportArchive/search/concern')
        downloadAsExcel(items.map((i) => buildReportRow(i, hasMultipleCompanies)), 'Organisationens beställningar')
    })

    return {
        isExporting,
        exportError,
        exportProgress,
        showProgressBar,
        exportSingleOrders,
        exportGroupOrders,
        exportOrganizationOrders,
    }
}
